import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Loader2, LogOut, CheckCircle2, AlertTriangle, Star } from "lucide-react";
import { portalApi } from "@/lib/portalApi";
import { useSeo } from "@/lib/seo";
import { Logo } from "@/components/Logo";
import { PortalFooter } from "@/components/PortalFooter";

const money = (n) => `$${Number(n || 0)}`;

function PaymentForm({ onDone, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true); setErr("");
    const { error, setupIntent } = await stripe.confirmSetup({ elements, redirect: "if_required" });
    if (error) { setErr(error.message || "Card could not be saved."); setBusy(false); return; }
    try {
      await portalApi.post(`/payment-methods/${setupIntent.payment_method}/default`);
      onDone();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not set default payment method.");
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="portal-payment-form">
      <PaymentElement />
      {err && <p className="text-sm text-red-600 dark:text-red-400" data-testid="portal-payment-error">{err}</p>}
      <div className="flex gap-2">
        <button type="submit" disabled={!stripe || busy} data-testid="portal-payment-submit"
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-md bg-foreground px-4 py-2.5 text-sm font-semibold text-background disabled:opacity-50">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}{busy ? "Saving…" : "Save card & continue"}
        </button>
        <button type="button" onClick={onCancel} className="rounded-md border border-border px-4 py-2.5 text-sm font-medium">Cancel</button>
      </div>
    </form>
  );
}

export default function PortalBilling() {
  useSeo({ title: "Plans & Billing", description: "Manage your MONOE business plan and billing.", path: "/business/billing" });
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [businesses, setBusinesses] = useState([]);
  const [bid, setBid] = useState("");
  const [plans, setPlans] = useState([]);
  const [featuredMonthly, setFeaturedMonthly] = useState(0);
  const [current, setCurrent] = useState(null);
  const [pageError, setPageError] = useState("");
  const [action, setAction] = useState("");       // in-flight action key
  const [notice, setNotice] = useState("");

  const [pay, setPay] = useState(null);           // { clientSecret, stripe, retry }

  const business = businesses.find((b) => (b.id || b.business_id) === bid);

  const bootStrap = useCallback(async () => {
    setLoading(true); setPageError("");
    try {
      const [{ data: biz }, { data: pl }] = await Promise.all([
        portalApi.get("/me/businesses"),
        portalApi.get("/plans"),
      ]);
      const list = Array.isArray(biz) ? biz : (biz.businesses || []);
      setBusinesses(list);
      setPlans(pl.plans || []);
      setFeaturedMonthly(pl.featured_monthly || 0);
      if (list.length > 0) setBid(list[0].id || list[0].business_id);
    } catch (err) {
      if (err?.response?.status === 401) return navigate("/business/login");
      setPageError("Could not load your account. Please try again.");
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { bootStrap(); }, [bootStrap]);

  const loadPlan = useCallback(async () => {
    if (!bid) return;
    try {
      const { data } = await portalApi.get(`/business/${bid}/plan`);
      setCurrent(data);
    } catch (err) {
      if (err?.response?.status === 401) return navigate("/business/login");
      setCurrent(null);
    }
  }, [bid, navigate]);

  useEffect(() => { loadPlan(); }, [loadPlan]);

  const logout = async () => { try { await portalApi.post("/auth/logout"); } finally { navigate("/business/login"); } };

  // Runs a billing mutation; if the backend needs a card (402), open Stripe and retry after save.
  const runWithPayment = async (key, fn) => {
    setAction(key); setNotice(""); setPageError("");
    try {
      await fn();
      await loadPlan();
      setNotice("Done. Your plan is up to date.");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 402) {
        try {
          const { data } = await portalApi.post("/stripe/setup-intent");
          const stripe = await loadStripe(data.publishable_key);
          setPay({ clientSecret: data.setup_client_secret, stripe, retry: fn, key });
        } catch {
          setPageError("A payment method is required but we couldn't start card entry. Please try again.");
        }
      } else if (status === 401) {
        navigate("/business/login");
      } else {
        setPageError(err?.response?.data?.detail || "That action couldn't be completed.");
      }
    } finally { setAction(""); }
  };

  const changePlan = (planId) => runWithPayment(`plan-${planId}`, () => portalApi.post(`/business/${bid}/plan`, { plan: planId }));
  const cancelPlan = () => { if (window.confirm("Cancel your paid plan? It stays active until the end of the paid period.")) runWithPayment("cancel", () => portalApi.post(`/business/${bid}/plan/cancel`)); };
  const buyFeatured = () => runWithPayment("featured", () => portalApi.post(`/business/${bid}/featured`, { months: 1 }));

  const onCardSaved = async () => {
    const retry = pay?.retry; setPay(null);
    if (retry) {
      setAction(pay.key);
      try { await retry(); await loadPlan(); setNotice("Done. Your plan is up to date."); }
      catch (e) { setPageError(e?.response?.data?.detail || "Saved your card, but the plan change failed. Please try again."); }
      finally { setAction(""); }
    }
  };

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-background"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  }

  const currentPlanId = current?.plan?.id || current?.plan_id;
  const status = current?.subscription_status;
  const cancelScheduled = current?.cancel_at_period_end;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-4">
          <Logo />
          <button onClick={logout} data-testid="portal-logout" className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm font-medium hover:bg-accent">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </header>

      <main className="mx-auto w-full max-w-4xl flex-1 px-4 py-8">
        {businesses.length === 0 ? (
          <div className="rounded-lg border border-border bg-card p-8 text-center" data-testid="portal-no-business">
            <p className="font-display text-lg font-semibold">This account doesn't own a business yet.</p>
            <p className="mt-2 text-sm text-muted-foreground">Create a business in the MONOE app to manage a plan here.</p>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="eyebrow text-muted-foreground">Business</p>
                {businesses.length > 1 ? (
                  <select data-testid="portal-business-select" value={bid} onChange={(e) => setBid(e.target.value)}
                    className="mt-1 rounded-md border border-border bg-card px-3 py-2 font-display text-lg font-semibold">
                    {businesses.map((b) => <option key={b.id || b.business_id} value={b.id || b.business_id}>{b.name || b.business_name}</option>)}
                  </select>
                ) : (
                  <h1 className="mt-1 font-display text-2xl font-bold tracking-tight" data-testid="portal-business-name">{business?.name || business?.business_name}</h1>
                )}
              </div>
              <div className="text-right" data-testid="portal-current-plan">
                <p className="eyebrow text-muted-foreground">Current plan</p>
                <p className="mt-1 font-display text-lg font-semibold">
                  {plans.find((p) => p.id === currentPlanId)?.name || currentPlanId || "—"}
                </p>
              </div>
            </div>

            {status === "past_due" && (
              <div className="mt-4 flex items-center gap-2 rounded-md border border-orange-500/30 bg-orange-500/10 px-4 py-3 text-sm text-orange-700 dark:text-orange-300" data-testid="portal-past-due">
                <AlertTriangle className="h-4 w-4" /> Payment failed — update your payment method.
              </div>
            )}
            {cancelScheduled && (
              <div className="mt-4 rounded-md border border-border bg-secondary px-4 py-3 text-sm text-muted-foreground" data-testid="portal-cancel-scheduled">
                Cancellation scheduled — your current plan remains active until the end of the paid period.
              </div>
            )}
            {notice && <div className="mt-4 flex items-center gap-2 rounded-md border border-emerald-600/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-700 dark:text-emerald-300" data-testid="portal-notice"><CheckCircle2 className="h-4 w-4" />{notice}</div>}
            {pageError && <div className="mt-4 rounded-md border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-700 dark:text-red-400" data-testid="portal-error">{pageError}</div>}

            {/* Plans */}
            <div className="mt-8 grid gap-4 sm:grid-cols-3">
              {plans.map((p) => {
                const isCurrent = p.id === currentPlanId;
                return (
                  <div key={p.id} data-testid={`portal-plan-${p.id}`} className={`flex flex-col rounded-lg border p-5 ${isCurrent ? "border-foreground" : "border-border"} bg-card`}>
                    <div className="flex items-center justify-between">
                      <h3 className="font-display text-base font-semibold">{p.name}</h3>
                      {isCurrent && <span className="rounded-full bg-foreground px-2 py-0.5 text-[10px] font-medium text-background">Current</span>}
                    </div>
                    <p className="mt-2 font-display text-2xl font-bold">{money(p.price)}<span className="text-sm font-normal text-muted-foreground">/mo</span></p>
                    <ul className="mt-4 flex-1 space-y-1.5 text-sm text-muted-foreground">
                      {(p.features || []).map((f, i) => <li key={i} className="flex gap-2"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-foreground" />{f}</li>)}
                    </ul>
                    <button disabled={isCurrent || !!action} onClick={() => changePlan(p.id)} data-testid={`portal-choose-${p.id}`}
                      className="mt-5 inline-flex items-center justify-center gap-2 rounded-md bg-foreground px-4 py-2.5 text-sm font-semibold text-background disabled:opacity-40">
                      {action === `plan-${p.id}` && <Loader2 className="h-4 w-4 animate-spin" />}
                      {isCurrent ? "Current plan" : (Number(p.price) > Number(plans.find((x) => x.id === currentPlanId)?.price || 0) ? "Upgrade" : "Change")}
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Sponsored placement */}
            <div className="mt-8 flex flex-col items-start justify-between gap-4 rounded-lg border border-border bg-card p-6 sm:flex-row sm:items-center" data-testid="portal-sponsored">
              <div className="flex items-start gap-3">
                <Star className="mt-0.5 h-5 w-5 text-foreground" />
                <div>
                  <p className="font-display text-base font-semibold">Sponsored Placement {current?.featured && <span className="ml-2 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] text-emerald-700 dark:text-emerald-300">Active</span>}</p>
                  <p className="mt-1 text-sm text-muted-foreground">Receive sponsored placement within eligible MONOE Discover surfaces. {money(featuredMonthly)}/month.</p>
                </div>
              </div>
              <button disabled={!!action} onClick={buyFeatured} data-testid="portal-buy-featured"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-foreground px-4 py-2.5 text-sm font-semibold hover:bg-accent disabled:opacity-40">
                {action === "featured" && <Loader2 className="h-4 w-4 animate-spin" />}Purchase 1 month
              </button>
            </div>

            {/* Cancel */}
            {currentPlanId && currentPlanId !== "free" && !cancelScheduled && (
              <button disabled={!!action} onClick={cancelPlan} data-testid="portal-cancel"
                className="mt-6 text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground disabled:opacity-40">
                Cancel subscription
              </button>
            )}
          </>
        )}
      </main>

      {pay && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" data-testid="portal-payment-modal">
          <div className="w-full max-w-md rounded-xl border border-border bg-card p-6">
            <h3 className="font-display text-lg font-semibold">Add a payment method</h3>
            <p className="mt-1 mb-4 text-sm text-muted-foreground">Securely enter your card to continue. Cards are handled by Stripe.</p>
            <Elements stripe={pay.stripe} options={{ clientSecret: pay.clientSecret, appearance: { theme: "stripe" } }}>
              <PaymentForm onDone={onCardSaved} onCancel={() => setPay(null)} />
            </Elements>
          </div>
        </div>
      )}

      <PortalFooter />
    </div>
  );
}
